import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ToastProvider, useToast } from './components/Toast';
import { ModalProvider, useModal } from './components/Modal';
import { AccountProvider, useAccount } from './contexts/AccountContext';
import axios from 'axios';
import {
  FaTachometerAlt, FaGasPump, FaUsers, FaExchangeAlt,
  FaTruck, FaSignOutAlt, FaEye, FaEyeSlash, FaBroadcastTower,
  FaLock, FaChartBar, FaBuilding, FaPlane, FaSun, FaMoon, FaClipboardCheck
} from 'react-icons/fa';
import Dashboard    from './pages/Dashboard';
import FuelLevels   from './pages/FuelLevels';
import Users        from './pages/Users';
import Transactions from './pages/Transactions';
import Tankers      from './pages/Tankers';
import LiveFueling  from './pages/LiveFueling';
import Statistics   from './pages/Statistics';
import Vendors      from './pages/Vendors';
import Aircrafts    from './pages/Aircrafts';
import BowserCheckups from './pages/BowserCheckups';
import './App.css';
import './styles/SignIn.css';

/* ─── PERMISSION ROUTE GUARD ──────────────────────────────────── */
function PermissionRoute({ perm, children }) {
  const { account } = useAccount();
  const ok = account?.admin || account?.permissions?.[perm];
  if (!ok) {
    return (
      <div className="page-container">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12, color: '#8896a5' }}>
          <FaLock style={{ fontSize: 36, color: '#db3131' }} />
          <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>Access Denied</div>
          <div style={{ fontSize: 14 }}>You don't have permission to view this page.</div>
        </div>
      </div>
    );
  }
  return children;
}

/* ─── SIGN-OUT CONFIRM ──────────────────────────────────────────── */
function SignoutConfirm({ closeModal, onSignout }) {
  return (
    <>
      <p><strong>Are you sure you want to sign out?</strong></p>
      <p className="warning-text">You will need to sign in again.</p>
    </>
  );
}

/* ─── LAYOUT (authenticated) ────────────────────────────────────── */
function Layout({ children }) {
  const location = useLocation();
  const { account, clearAccount } = useAccount();
  const { openModal } = useModal();

  const [theme, setTheme] = useState(() => localStorage.getItem('uas-theme') || 'dark');
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme === 'light' ? 'light' : '');
    localStorage.setItem('uas-theme', theme);
  }, [theme]);
  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  const navGroups = [
    { section: 'Monitoring', items: [
      { to: '/',             icon: <FaTachometerAlt />, label: 'Dashboard',    perm: 'dashboard' },
      { to: '/live-fueling', icon: <FaBroadcastTower />, label: 'Live Fueling', perm: 'live_fueling' },
    ]},
    { section: 'Records', items: [
      { to: '/fuel-levels',       icon: <FaGasPump />,        label: 'Fuel Levels',          perm: 'fuel_levels' },
      { to: '/transactions',      icon: <FaExchangeAlt />,    label: 'Transactions',         perm: 'transactions' },
      { to: '/bowser-checkups',   icon: <FaClipboardCheck />, label: 'Bowser Checkups',      perm: 'bowser_checkups' },
    ]},
    { section: 'Analytics', items: [
      { to: '/statistics',   icon: <FaChartBar />,       label: 'Statistics',   perm: 'statistics' },
    ]},
    { section: 'Management', items: [
      { to: '/tankers',    icon: <FaTruck />,    label: 'Tankers',   perm: 'tankers' },
      { to: '/vendors',    icon: <FaBuilding />, label: 'Vendors',   perm: 'reference' },
      { to: '/aircrafts',  icon: <FaPlane />,    label: 'Aircraft',  perm: 'reference' },
      { to: '/users',      icon: <FaUsers />,    label: 'Users',     perm: 'users' },
    ]},
  ];

  const canAccess = (perm) => account?.admin || account?.permissions?.[perm];

  const isActive = (to) => to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const handleSignout = () => {
    openModal('Sign out', <SignoutConfirm onSignout={clearAccount} />, 'sm', {
      footer: (closeModal) => (
        <>
          <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { clearAccount(); closeModal(); }}>Sign Out</button>
        </>
      )
    });
  };

  return (
    <div className="app-container">
      <nav className="sidebar">
        <div className="sidebar-header">
          <div className="logo">
            <img
              src={theme === 'dark' ? '/assets/logo_white.png' : '/assets/logo_black.png'}
              alt="UAS Admin"
              className="logo-image"
            />
            <span className="logo-text">
              <span className="logo-accent">Admin</span>
            </span>
          </div>
        </div>

        <div className="nav-menu">
          {navGroups.map(group => {
            const visible = group.items.filter(item => canAccess(item.perm));
            if (visible.length === 0) return null;
            return (
              <React.Fragment key={group.section}>
                <div className="nav-title">{group.section}</div>
                {visible.map(item => (
                  <Link key={item.to} to={item.to} className={`nav-item ${isActive(item.to) ? 'active' : ''}`}>
                    <span className="nav-icon">{item.icon}</span>
                    <span>{item.label}</span>
                  </Link>
                ))}
              </React.Fragment>
            );
          })}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">{(account?.name || account?.email)?.charAt(0).toUpperCase()}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{account?.name || account?.email}</div>
              <div className="sidebar-user-role">{account?.role || 'Admin'}</div>
            </div>
          </div>
          <button className="theme-toggle-btn" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            {theme === 'dark' ? <FaSun /> : <FaMoon />}
          </button>
          <button className="signout-btn" onClick={handleSignout} title="Sign out">
            <FaSignOutAlt />
          </button>
        </div>
      </nav>

      <main className="main-content">
        {children}
      </main>
    </div>
  );
}

/* ─── APP INNER (handles auth check) ───────────────────────────── */
function AppInner() {
  const { account, updateAccount } = useAccount();
  const { showToast } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}auth/signin`,
        { email: email.trim(), password: password.trim() },
        {
          headers: {
            'Content-Type': 'application/json',
            'api': process.env.REACT_APP_API_KEY,
          },
        }
      );
      const data = res.data;
      if (data?.success && data?.data?.account) {
        updateAccount(data.data.account);
      } else {
        setError(data?.response || 'Invalid email or password.');
      }
    } catch (err) {
      setError('Could not connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!account) {
    return (
      <div className="signin-container">
        <div className="signin-card">
          <div className="signin-header">
            <div className="logo-signin" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
              <img
                src={(localStorage.getItem('uas-theme') || 'dark') === 'dark' ? '/assets/logo_white.png' : '/assets/logo_black.png'}
                alt="UAS"
                className="logo-image"
                style={{ height: 48 }}
              />
              <span className="logo-text large">
                <span className="logo-accent">Admin</span>
              </span>
            </div>
            <h2>Sign in to UAS Admin</h2>
            <p>Fuel management &amp; operations dashboard</p>
          </div>
          <form className="signin-form" onSubmit={handleSignIn}>
            {error && <div className="form-error">{error}</div>}
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-input" placeholder="admin@example.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSignIn(e); }}
                  required
                  autoComplete="current-password"
                  style={{ paddingRight: 45 }}
                />
                <button type="button" onClick={() => setShowPwd(p => !p)} style={{ position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'#9ca3af',fontSize:18,display:'flex' }}>
                  {showPwd ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <button type="submit" className="btn btn-primary signin-button" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          <div className="signin-footer">
            <p>UAS Fuel Management &amp; Operations</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/"             element={<PermissionRoute perm="dashboard"><Dashboard /></PermissionRoute>} />
        <Route path="/fuel-levels"  element={<PermissionRoute perm="fuel_levels"><FuelLevels /></PermissionRoute>} />
        <Route path="/transactions" element={<PermissionRoute perm="transactions"><Transactions /></PermissionRoute>} />
        <Route path="/tankers"      element={<PermissionRoute perm="tankers"><Tankers /></PermissionRoute>} />
        <Route path="/live-fueling" element={<PermissionRoute perm="live_fueling"><LiveFueling /></PermissionRoute>} />
        <Route path="/statistics"   element={<PermissionRoute perm="statistics"><Statistics /></PermissionRoute>} />
        <Route path="/bowser-checkups" element={<PermissionRoute perm="bowser_checkups"><BowserCheckups /></PermissionRoute>} />
        <Route path="/vendors"         element={<PermissionRoute perm="reference"><Vendors /></PermissionRoute>} />
        <Route path="/aircrafts"        element={<PermissionRoute perm="reference"><Aircrafts /></PermissionRoute>} />
        <Route path="/users"        element={<PermissionRoute perm="users"><Users /></PermissionRoute>} />
      </Routes>
    </Layout>
  );
}

/* ─── ROOT ──────────────────────────────────────────────────────── */
export default function App() {
  return (
    <BrowserRouter>
      <AccountProvider>
        <ToastProvider>
          <ModalProvider>
            <AppInner />
          </ModalProvider>
        </ToastProvider>
      </AccountProvider>
    </BrowserRouter>
  );
}
